import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Play, Pause, Volume2, VolumeX, Loader2, Maximize2 } from 'lucide-react';
import introVideo from '../assets/intro-movie/IMG_0869.MP4';

interface HeroProps {
  onVideoReady?: () => void;
}

const Hero: React.FC<HeroProps> = ({ onVideoReady }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const toggleFullscreen = useCallback(() => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  }, []);

  const handleVideoEnd = useCallback(() => {
    if (videoRef.current) {
      // Video sonundan 3 saniye önceye git ve oynat
      const targetTime = Math.max(0, videoRef.current.duration - 3);
      videoRef.current.currentTime = targetTime;
      videoRef.current.play();
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      
      // Video sonuna 3 saniye kala döngüye başla
      if (duration > 0 && currentTime >= duration - 3) {
        videoRef.current.currentTime = 0;
      }
      
      setCurrentTime(currentTime);
    }
  }, []);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoadedData = () => {
        console.log('Video loaded successfully');
        setIsLoading(false);
        setHasError(false);
        setDuration(video.duration);
        onVideoReady?.();
      };
      const handleError = (e: Event) => {
        console.error('Video error:', e);
        setIsLoading(false);
        setHasError(true);
        onVideoReady?.();
      };
      
      // Timeout for loading
      const loadingTimeout = setTimeout(() => {
        if (isLoading) {
          console.log('Video loading timeout');
          setIsLoading(false);
          setHasError(true);
          onVideoReady?.();
        }
      }, 5000); // 5 saniye sonra timeout
      
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
      video.addEventListener('timeupdate', handleTimeUpdate);
      
      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        clearTimeout(loadingTimeout);
      };
    }
  }, [isLoading, handleTimeUpdate, onVideoReady]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      } else if (e.code === 'KeyM') {
        e.preventDefault();
        toggleMute();
      } else if (e.code === 'KeyF') {
        e.preventDefault();
        toggleFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [togglePlay, toggleMute, toggleFullscreen]);

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden bg-black pt-16 md:pt-0">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20">
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="text-white mb-4"
              >
                <Loader2 size={48} />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="text-white text-lg font-light mb-2 drop-shadow-lg"
              >
                Video yükleniyor...
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 1 }}
                className="text-white/80 text-sm font-light drop-shadow-sm"
              >
                Mağazamızın dışarıdan çekimini izleyin
              </motion.div>
            </div>
          </div>
        )}
        
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20">
            <div className="text-center text-white">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-6xl mb-4 drop-shadow-lg"
              >
                🎬
              </motion.div>
              <p className="text-xl font-light mb-4 drop-shadow-lg">Video yüklenemedi</p>
              <p className="text-gray-300 mb-4 drop-shadow-sm">Mağazamızın dışarıdan çekimi</p>
              <motion.button
                onClick={() => {
                  setHasError(false);
                  setIsLoading(true);
                  if (videoRef.current) {
                    videoRef.current.load();
                  }
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/95 backdrop-blur-sm text-black px-6 py-3 rounded-full font-medium hover:bg-white transition-colors shadow-2xl hover:shadow-white/25"
              >
                Tekrar Dene
              </motion.button>
            </div>
          </div>
        )}

        {/* Fallback Image when video fails */}
        {hasError && (
          <div className="absolute inset-0 w-full h-full">
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 25% 25%, #ffffff 2px, transparent 2px),
                                    radial-gradient(circle at 75% 75%, #ffffff 2px, transparent 2px)`,
                  backgroundSize: '100px 100px'
                }} />
              </div>
            </div>
            
            {/* Content Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-8xl mb-6"
                >
                  🏪
                </motion.div>
                <h2 className="text-4xl font-light mb-4">Kaçkar Karafırın</h2>
                <p className="text-xl text-gray-300 font-light mb-6">Mağazamızın dışarıdan çekimi</p>
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-2xl"
                >
                  🥖 🍰 🍪
                </motion.div>
              </div>
            </div>
          </div>
        )}

        <video
          ref={videoRef}
          autoPlay
          muted={true}
          loop
          playsInline
          preload="metadata"
          poster="/logo192.png"
          onEnded={handleVideoEnd}
          onLoadStart={() => console.log('Video load started')}
          onCanPlay={() => {
            console.log('Video can play');
            setIsLoading(false);
            onVideoReady?.();
          }}
          onError={(e) => {
            console.error('Video error:', e);
            setHasError(true);
            onVideoReady?.();
          }}
          className="w-full h-full object-cover"
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(false)}
          onTouchStart={() => setShowControls(true)}
          onTouchEnd={() => setTimeout(() => setShowControls(false), 3000)}
          style={{
            willChange: 'transform',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden'
          }}
          aria-label="Kaçkar Karafırın tanıtım videosu"
          role="banner"
        >
          <source src={introVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-black/60"></div>
        
        {/* Enhanced Video Controls */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showControls ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="absolute bottom-4 md:bottom-8 left-4 md:left-8 right-4 md:right-8 z-30"
        >
          {/* Progress Bar */}
          <div className="mb-3 md:mb-4">
            <div className="w-full bg-white/20 rounded-full h-1.5 backdrop-blur-sm">
              <motion.div
                className="bg-gradient-to-r from-white to-gray-200 h-1.5 rounded-full shadow-lg"
                style={{ width: `${(currentTime / duration) * 100}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>
          </div>
          
          {/* Controls Bar */}
          <div className="flex items-center justify-between bg-black/60 backdrop-blur-md rounded-full px-4 md:px-6 py-2 md:py-3 border border-white/10">
            {/* Left Controls */}
            <div className="flex items-center space-x-2 md:space-x-4">
              <button
                onClick={togglePlay}
                className="text-white hover:text-gray-200 transition-colors p-1 md:p-2 hover:bg-white/10 rounded-full"
                aria-label={isPlaying ? 'Videoyu durdur' : 'Videoyu oynat'}
              >
                {isPlaying ? <Pause size={18} className="md:w-5 md:h-5" /> : <Play size={18} className="md:w-5 md:h-5" />}
              </button>
              
              <button
                onClick={toggleMute}
                className="text-white hover:text-gray-200 transition-colors p-1 md:p-2 hover:bg-white/10 rounded-full"
                aria-label={isMuted ? 'Sesi aç' : 'Sesi kapat'}
              >
                {isMuted ? <VolumeX size={18} className="md:w-5 md:h-5" /> : <Volume2 size={18} className="md:w-5 md:h-5" />}
              </button>
              
              <div className="text-white text-xs md:text-sm font-light hidden sm:block drop-shadow-sm">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>
            
            {/* Right Controls */}
            <div className="flex items-center space-x-2 md:space-x-4">
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-gray-200 transition-colors p-1 md:p-2 hover:bg-white/10 rounded-full"
                aria-label="Tam ekran yap"
              >
                <Maximize2 size={18} className="md:w-5 md:h-5" />
              </button>
            </div>
          </div>
          
          {/* Mobile Hint */}
          <div className="text-center mt-2 sm:hidden">
            <p className="text-white/70 text-xs font-light drop-shadow-sm">
              Kontroller için videoya dokunun
            </p>
          </div>
        </motion.div>
      </div>

      {/* Hero Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Main heading */}
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="text-center mb-12 md:mb-20"
          >
            {/* Main Title - KAÇKAR */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.1, ease: "easeOut" }}
              className="relative mb-5 md:mb-8"
            >
              <div className="text-4xl sm:text-5xl md:text-8xl font-extralight leading-none tracking-[0.2em] sm:tracking-[0.28em] md:tracking-[0.4em] text-amber-100 mb-8 md:mb-16">
                <span className="text-amber-100 relative">
                  KAÇKAR
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-100/20 via-amber-100/10 to-amber-100/20 blur-3xl -z-10"></div>
                </span>
              </div>
              
              {/* Decorative line under KAÇKAR */}
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.5, delay: 0.8, ease: "easeOut" }}
                className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 h-px bg-gradient-to-r from-transparent via-amber-200/60 to-transparent"
                style={{ width: "50%" }}
              />
            </motion.div>
            
            {/* Subtitle - KARAFIRIN */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
              className="text-base sm:text-lg md:text-2xl font-light tracking-[0.14em] md:tracking-[0.2em] text-amber-200/90 mt-4 md:mt-8"
            >
              KARAFIRIN
            </motion.div>
          </motion.h1>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
            className="text-center mb-20 md:mb-32"
          >
            <motion.p
              className="text-sm sm:text-base md:text-lg text-amber-200/80 max-w-xl mx-auto leading-relaxed font-light mt-8 md:mt-12 tracking-wide"
            >
              Geleneksel lezzetlerin modern sunumu.
            </motion.p>
            
            {/* Decorative elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 1, ease: "easeOut" }}
              className="flex justify-center items-center space-x-3 mt-6 md:mt-8 mb-8 md:mb-20"
            >
              <div className="w-1.5 h-1.5 bg-amber-200/60 rounded-full"></div>
              <div className="w-1 h-1 bg-amber-300/70 rounded-full"></div>
              <div className="w-1.5 h-1.5 bg-amber-200/60 rounded-full"></div>
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, delay: 1.2 }}
            className="absolute bottom-4 md:bottom-12 left-1/2 transform -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="text-white/90 drop-shadow-lg"
            >
              <div className="flex flex-col items-center">
                <ChevronDown size={32} className="mb-2" />
                <div className="w-0.5 h-8 bg-gradient-to-b from-amber-200/60 to-transparent"></div>
                <motion.p
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-xs text-amber-200/80 font-light tracking-wider mt-2"
                >
                  KAYDIR
                </motion.p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 